import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../components/AdminLayout';
import LoadingSpinner from '../components/LoadingSpinner';
import AuditTraceExplorer from '../components/aiAudit/AuditTraceExplorer';
import TurnCodeTab from '../components/aiAudit/TurnCodeTab';
import AgentInsightsTab from '../components/aiAudit/AgentInsightsTab';
import toast from 'react-hot-toast';
import {
  ArrowLeftIcon,
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const statusClasses = {
  success: 'bg-green-100 text-green-800',
  partial: 'bg-yellow-100 text-yellow-800',
  failed: 'bg-red-100 text-red-800',
  error: 'bg-red-100 text-red-800',
  no_change: 'bg-gray-100 text-gray-700',
  ask: 'bg-sky-100 text-sky-800',
  pending_user_choice: 'bg-blue-100 text-blue-800',
};

const statusLabels = {
  success: 'Success',
  partial: 'Partial',
  failed: 'Failed',
  error: 'Error',
  no_change: 'No change',
  ask: 'Ask',
  pending_user_choice: 'User choice',
};

const modeClasses = {
  ask: 'bg-sky-50 text-sky-700 ring-sky-200',
  change: 'bg-gray-50 text-gray-600 ring-gray-200',
  legal: 'bg-violet-50 text-violet-700 ring-violet-200',
};

const modeLabels = {
  ask: 'Ask mode',
  change: 'Change mode',
  legal: 'Legal mode',
};

const modeHelpers = {
  ask: 'Informational reply; no site mutation expected',
  change: 'Mutation-capable editor request',
  legal: 'Legal advisor request',
};

// Which AI surface produced the turn. Rows written before the studio was
// audited have no surface and are website-editor turns by definition.
const surfaceClasses = {
  website_editor: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
  crm_ui_studio: 'bg-fuchsia-50 text-fuchsia-700 ring-fuchsia-200',
};

const surfaceLabels = {
  website_editor: 'Website editor',
  crm_ui_studio: 'BOS Design Studio',
};

const surfaceHelpers = {
  website_editor: 'Editor AI assistant turn',
  crm_ui_studio: 'BOS skin / capability build',
};

const outcomeClasses = {
  hard_error: 'bg-red-100 text-red-800',
  request_failure: 'bg-rose-100 text-rose-800',
  action_failure: 'bg-orange-100 text-orange-800',
  verification_failure: 'bg-purple-100 text-purple-800',
  guardrail_rejection: 'bg-amber-100 text-amber-800',
  recovered_partial: 'bg-yellow-100 text-yellow-800',
  recovered_success: 'bg-emerald-100 text-emerald-800',
};

const outcomeLabels = {
  hard_error: 'Hard error',
  request_failure: 'Request failure',
  action_failure: 'Action failure',
  verification_failure: 'Verification failure',
  guardrail_rejection: 'Guardrail rejection',
  recovered_partial: 'Recovered partial',
  recovered_success: 'Recovered success',
};

function formatDuration(ms) {
  if (!ms && ms !== 0) return '—';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function getAuditMode(audit) {
  return audit?.advisor_mode || 'change';
}

function getAuditSurface(audit) {
  return audit?.surface || 'website_editor';
}

function getDisplayStatus(audit) {
  const mode = getAuditMode(audit);
  if (mode === 'ask' && audit?.status === 'no_change') return 'ask';
  return audit?.status || 'no_change';
}

function StatusBadge({ audit }) {
  const status = getDisplayStatus(audit);
  return (
    <span className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full ${statusClasses[status] || statusClasses.no_change}`}>
      {statusLabels[status] || status}
    </span>
  );
}

function ModeBadge({ mode }) {
  const key = mode || 'change';
  return (
    <span className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full ring-1 ${modeClasses[key] || modeClasses.change}`}>
      {modeLabels[key] || `${key} mode`}
    </span>
  );
}

function SurfaceBadge({ surface }) {
  const key = surface || 'website_editor';
  return (
    <span className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full ring-1 ${surfaceClasses[key] || surfaceClasses.website_editor}`}>
      {surfaceLabels[key] || key}
    </span>
  );
}

function OutcomeBadge({ outcome }) {
  if (!outcome) return null;
  return (
    <span className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full ${outcomeClasses[outcome] || 'bg-gray-100 text-gray-700'}`}>
      {outcomeLabels[outcome] || outcome}
    </span>
  );
}

const STUDIO_TURN_MODE_LABELS = {
  tweak: 'Tweak',
  new_feature: 'New feature',
  redesign: 'Redesign',
};

function studioSmokeLabel(smoke) {
  if (!smoke || typeof smoke !== 'object') return '—';
  if (smoke.ok) {
    const rendered = Number(smoke.rendered) || 0;
    return rendered ? `Passed · ${rendered} screens` : 'Passed';
  }
  const errors = Number(smoke.errors) || 0;
  return errors ? `Failed · ${errors} errors` : 'Failed';
}

function studioCritiqueLabel(critique) {
  if (!critique || typeof critique !== 'object') return '—';
  if (critique.passes === true) return 'Passed';
  if (critique.passes === false) return 'Revise';
  return '—';
}

function studioEndingLabel(audit, meta) {
  if (meta?.aborted) return 'Stopped by the user';
  if (meta?.roundBudgetExhausted) return 'Hit the round budget';
  if (audit?.status === 'error' || audit?.status === 'failed') return 'Stopped';
  return 'Finished';
}

function StudioQuality({ audit, studioMeta }) {
  const misses = Number(studioMeta.editFilesMissCount) || 0;
  const cells = [
    { label: 'Turn mode', value: STUDIO_TURN_MODE_LABELS[studioMeta.turnMode] || '—' },
    { label: 'Compile attempts', value: studioMeta.compileAttempts || 0 },
    { label: 'Edit misses', value: misses },
    { label: 'Smoke render', value: studioSmokeLabel(studioMeta.smokeRender) },
    { label: 'Design critique', value: studioCritiqueLabel(studioMeta.designCritique) },
    { label: 'How it ended', value: studioEndingLabel(audit, studioMeta) },
  ];
  return (
    <div className="bg-white rounded-lg shadow p-4 mb-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-3">Studio quality</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3 text-sm">
        {cells.map((cell) => (
          <div key={cell.label} className="rounded-lg bg-gray-50 p-3">
            <p className="text-gray-700 font-medium">{cell.label}</p>
            <p className="mt-1 text-xl font-semibold text-gray-900 leading-snug">{cell.value}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function SummaryCard({ label, value, helper }) {
  return (
    <div className="bg-white rounded-lg shadow p-4">
      <p className="text-sm text-gray-500">{label}</p>
      <p className="mt-1 text-2xl font-semibold text-gray-900">{value ?? '—'}</p>
      {helper && <p className="mt-1 text-xs text-gray-500">{helper}</p>}
    </div>
  );
}

function classifyActionFailure(action) {
  if (action?.failureCategory === 'verification_failure') return 'verificationFailures';
  if (action?.failureCategory === 'guardrail_rejection') return 'guardrails';

  const text = `${action?.error || ''} ${action?.evidenceReason || ''}`.toLowerCase();
  if (/navigation timeout|preview navigation|timed out|timeout exceeded|timeout of \d+ ms exceeded/.test(text)) {
    return 'verificationFailures';
  }
  if (/unscoped selector blocked|css safety violations|css modification violations|blocked property|unsafe value|cascade-trap blocked|guardrail|forbidden|!important/.test(text)) {
    return 'guardrails';
  }
  return 'mutationFailures';
}

const VIEWS = [
  { id: 'summary', label: 'Summary' },
  { id: 'trace', label: 'Trace' },
  { id: 'user', label: 'As the user saw it' },
  { id: 'ai', label: 'As the AI saw it' },
  { id: 'metadata', label: 'Metadata' },
];

const AICustomizationAuditDetail = () => {
  const { id } = useParams();
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('trace');

  const fetchAudit = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/admin/ai-customizations/${id}`);
      setAudit(response.data.data.audit);
    } catch (error) {
      console.error('Error fetching AI audit:', error);
      toast.error('Failed to load AI audit');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchAudit();
  }, [fetchAudit]);

  if (loading) {
    return (
      <AdminLayout>
        <div className="py-12"><LoadingSpinner /></div>
      </AdminLayout>
    );
  }

  if (!audit) {
    return (
      <AdminLayout>
        <div className="text-center py-12">
          <p className="text-gray-500">Audit record not found</p>
          <Link to="/ai-customizations" className="text-primary-600 hover:underline mt-2 inline-block">
            Back to AI Audit
          </Link>
        </div>
      </AdminLayout>
    );
  }

  const failedActions = Array.isArray(audit.failed_actions) ? audit.failed_actions : [];
  const auditMode = getAuditMode(audit);
  const auditSurface = getAuditSurface(audit);
  const isStudio = auditSurface === 'crm_ui_studio';
  const studioMeta = (isStudio && audit.metadata) || {};
  const failureBreakdown = failedActions.reduce((acc, action) => {
    acc[classifyActionFailure(action)] += 1;
    return acc;
  }, { mutationFailures: 0, verificationFailures: 0, guardrails: 0 });
  const retrySignals = [
    audit.retry_passes > 0 && `${audit.retry_passes} retry pass(es)`,
    audit.follow_up_rounds > 0 && `${audit.follow_up_rounds} follow-up round(s)`,
    audit.done_gate_nudged && 'done gate nudged',
    audit.intent_checks_used > 0 && `${audit.intent_checks_used} intent check(s)`,
  ].filter(Boolean);

  const promptLine = String(audit.prompt || '').replace(/\s+/g, ' ').trim();
  const listHref = isStudio
    ? '/ai-customizations?surface=crm_ui_studio'
    : '/ai-customizations?surface=website_editor';

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Link
              to={listHref}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeftIcon className="w-5 h-5" />
            </Link>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold text-gray-900">AI Audit Turn</h1>
                <StatusBadge audit={audit} />
                <SurfaceBadge surface={auditSurface} />
                {/* advisor_mode is an editor concept — a studio turn has no mode. */}
                {!isStudio && <ModeBadge mode={auditMode} />}
                <OutcomeBadge outcome={audit.outcome_label} />
              </div>
              <p className="text-sm text-gray-500">
                {new Date(audit.created_at).toLocaleString()}
                {audit.turn_id && <span className="ml-2 font-mono">turn {audit.turn_id}</span>}
              </p>
              {promptLine && (
                <p className="mt-1 text-sm text-gray-700 max-w-3xl truncate" title={promptLine}>
                  {promptLine}
                </p>
              )}
            </div>
          </div>
          <button
            onClick={fetchAudit}
            disabled={loading}
            className="p-2 text-gray-600 hover:text-primary-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
            title="Refresh"
          >
            <ArrowPathIcon className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-gray-500">Website</p>
              {audit.website_name ? (
                <Link to={`/websites/${audit.website_id}`} className="font-medium text-primary-700 hover:underline">
                  {audit.website_name}
                </Link>
              ) : (
                <p className="font-medium text-gray-700">{audit.account_name || 'No site'}</p>
              )}
            </div>
            <div>
              <p className="text-gray-500">User</p>
              <p className="font-medium text-gray-700">{audit.user_email || audit.user_name || 'Unknown'}</p>
            </div>
            <div>
              <p className="text-gray-500">Account</p>
              {audit.account_id ? (
                <Link to={`/accounts/${audit.account_id}`} className="font-medium text-primary-700 hover:underline">
                  {audit.account_name || audit.account_id}
                </Link>
              ) : (
                <p className="font-medium text-gray-700">Unknown</p>
              )}
            </div>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            {audit.website_name && (
              <Link to={`/open-editor/${audit.website_id}`} className="btn-secondary text-sm inline-flex items-center gap-1.5">
                <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                Open Editor
              </Link>
            )}
            {audit.website_name && (
              <Link to={`/websites/${audit.website_id}`} className="btn-secondary text-sm">
                Website Detail
              </Link>
            )}
            {!audit.website_name && audit.account_id && (
              <Link to={`/accounts/${audit.account_id}`} className="btn-secondary text-sm">
                Account Detail
              </Link>
            )}
          </div>
        </div>

        <div className="mb-6 border-b border-gray-200">
          <nav className="flex flex-wrap gap-1" aria-label="Audit views">
            {(isStudio
              ? [VIEWS[0], VIEWS[1], { id: 'code', label: 'Code' }, { id: 'insights', label: 'Agent insights' }, ...VIEWS.slice(2)]
              : VIEWS
            ).map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => setView(item.id)}
                className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                  view === item.id
                    ? 'border-primary-600 text-primary-700'
                    : 'border-transparent text-gray-500 hover:text-gray-800'
                }`}
              >
                {item.label}
              </button>
            ))}
          </nav>
        </div>

        {view !== 'summary' && view !== 'code' && view !== 'insights' && (
          <AuditTraceExplorer audit={audit} view={view} />
        )}

        {view === 'code' && isStudio && (
          <TurnCodeTab auditId={audit.id} />
        )}

        {view === 'insights' && isStudio && (
          <AgentInsightsTab auditId={audit.id} />
        )}

        {view === 'summary' && (
        <>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-7 gap-4 mb-6">
          {isStudio ? (
            <SummaryCard label="Surface" value={surfaceLabels[auditSurface]} helper={surfaceHelpers[auditSurface]} />
          ) : (
            <SummaryCard label="Mode" value={modeLabels[auditMode] || auditMode} helper={modeHelpers[auditMode] || null} />
          )}
          <SummaryCard label="Outcome" value={outcomeLabels[audit.outcome_label] || audit.outcome_label || '—'} helper={retrySignals.length ? retrySignals.join(', ') : 'No recovery signals'} />
          <SummaryCard label="Model Rounds" value={audit.model_rounds_attempted} helper={`${audit.follow_up_rounds || 0} follow-up round(s)`} />
          {/* Intent checks and screenshots are editor-loop mechanisms; for a
              studio turn the durable output is the version it shipped. */}
          {isStudio ? (
            <SummaryCard
              label="Skin Version"
              value={audit.new_version ?? '—'}
              helper={studioMeta.versionCreated ? 'Compiled and published a version' : 'No new version'}
            />
          ) : (
            <SummaryCard label="Visions" value={audit.intent_checks_used || 0} helper={`${audit.visual_verification_count || 0} verification event(s)`} />
          )}
          {isStudio ? (
            <SummaryCard
              label="BOS Changes"
              value={[studioMeta.capabilitiesChanged && 'capabilities', studioMeta.configChanged && 'config'].filter(Boolean).join(' + ') || '—'}
              helper={studioMeta.roundBudgetExhausted ? 'Hit the round budget' : (studioMeta.aborted ? 'Stopped by the user' : 'Definitions and config')}
            />
          ) : (
            <SummaryCard label="Screenshots" value={audit.screenshots_captured || 0} helper={`${audit.before_screenshot_captured ? 'before' : 'no before'} / ${audit.after_screenshot_captured ? 'after' : 'no after'}`} />
          )}
          <SummaryCard label="Tool Calls" value={audit.tool_calls_count || 0} helper={`${audit.mutation_count || 0} mutation(s)`} />
          <SummaryCard label="Duration" value={formatDuration(audit.duration_ms)} helper={audit.provider ? `${audit.provider}${audit.model ? ` / ${audit.model}` : ''}` : null} />
        </div>

        {isStudio ? (
          <StudioQuality audit={audit} studioMeta={studioMeta} />
        ) : (
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Outcome Breakdown</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3 text-sm">
            <div className="rounded-lg bg-orange-50 p-3">
              <p className="text-orange-700 font-medium">Mutation failures</p>
              <p className="text-2xl font-semibold text-orange-900">{failureBreakdown.mutationFailures}</p>
            </div>
            <div className="rounded-lg bg-purple-50 p-3">
              <p className="text-purple-700 font-medium">Verification failures</p>
              <p className="text-2xl font-semibold text-purple-900">{failureBreakdown.verificationFailures}</p>
            </div>
            <div className="rounded-lg bg-amber-50 p-3">
              <p className="text-amber-700 font-medium">Guardrails</p>
              <p className="text-2xl font-semibold text-amber-900">{failureBreakdown.guardrails}</p>
            </div>
            <div className="rounded-lg bg-blue-50 p-3">
              <p className="text-blue-700 font-medium">Retry passes</p>
              <p className="text-2xl font-semibold text-blue-900">{audit.retry_passes || 0}</p>
            </div>
            <div className="rounded-lg bg-indigo-50 p-3">
              <p className="text-indigo-700 font-medium">Intent escalation</p>
              <p className="text-2xl font-semibold text-indigo-900">{audit.done_gate_nudged ? 'Yes' : 'No'}</p>
            </div>
            <div className="rounded-lg bg-gray-50 p-3">
              <p className="text-gray-700 font-medium">Intent exhaustion</p>
              <p className="text-2xl font-semibold text-gray-900">{audit.intent_checks_used >= 2 ? 'Likely' : 'No'}</p>
            </div>
          </div>
        </div>
        )}

        {(audit.error_message || failedActions.length > 0) && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <ExclamationCircleIcon className="w-5 h-5 text-red-600 mt-0.5" />
              <div>
                <h2 className="text-sm font-semibold text-red-900">Failure Evidence</h2>
                {audit.error_message && <p className="mt-1 text-sm text-red-800">{audit.error_message}</p>}
                {failedActions.length > 0 && (
                  <ul className="mt-2 space-y-1 text-sm text-red-800">
                    {failedActions.map((action, index) => (
                      <li key={`${action.action || 'action'}-${index}`}>
                        <span className="font-mono">{action.action || 'unknown'}</span>
                        {action.error && <span>: {action.error}</span>}
                        {action.evidenceReason && <span>: {action.evidenceReason}</span>}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        )}
        </>
        )}
      </div>
    </AdminLayout>
  );
};

export default AICustomizationAuditDetail;
